// src/pages/components/MateriaisRiscoDashboard.js
import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";

import MenuSearchMateriais from "pages/components/MenuSearchMateriais";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import InfoIcon from "@mui/icons-material/Info";

export default function MateriaisRiscoDashboard({ podeVerRuturas }) {
  const { showAlert } = useAlert();

  const [materiaisRisco, setMateriaisRisco] = useState([]);
  const [loading, setLoading] = useState(false);

  const [mostrarInativos, setMostrarInativos] = useState(false);

  // ---------------------------------------------------------------------------
  // diálogos de ATIVAR MATERIAL
  // ---------------------------------------------------------------------------
  const abrirDialogAtivar = (mat) => {
    setMaterialRiscoSel(mat);
    setOpenDialogAtivar(true);
  };

  const handleCancelarAtivar = () => {
    setOpenDialogAtivar(false);
    setMaterialRiscoSel(null);
  };

  const handleConfirmarAtivar = async () => {
    if (!materialRiscoSel) return;

    try {
      const body = {
        refmaterial: materialRiscoSel.refmaterial,
        inativo: 0,
      };

      const res = await api.post("Materiais/UpdMaterialInativo", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Material ativado com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) => {
        // se estiver desligado, o material já não está na lista (porque estava inativo)
        // mas se por acaso estiver, atualiza-o:
        const atualizada = prev.map((m) =>
          m.refmaterial === materialRiscoSel.refmaterial ? { ...m, inativo: 0 } : m
        );

        return atualizada;
      });
    } catch {
      showAlert("Erro ao ativar material.", "error");
    } finally {
      handleCancelarAtivar();
    }
  };

  // diálogos de INATIVAR / DIAS RUTURA
  const [openDialogInativar, setOpenDialogInativar] = useState(false);
  const [materialRiscoSel, setMaterialRiscoSel] = useState(null);
  const [openDialogAtivar, setOpenDialogAtivar] = useState(false);

  const [imgsByRef, setImgsByRef] = useState({});

  const [dialogDiasRutura, setDialogDiasRutura] = useState({
    open: false,
    refmaterial: "",
    nome: "",
    valorAntigo: 30,
    valorNovo: 30,
  });

  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [materialDetalhe, setMaterialDetalhe] = useState(null);

  // tooltips iguais aos de AnaliseStocks
  const tooltipMediaAno = useMemo(
    () => (
      <div style={{ fontSize: 11, maxWidth: 360 }}>
        <strong>Dias Rutura (média ano)</strong>
        <br />
        Calculado com:
        <br />
        • Vendas do material nos últimos 365 dias (até à data atual);
        <br />
        • Soma das quantidades vendidas nesse período;
        <br />
        • Consumo diário = total vendido / nº de dias do período;
        <br />• Dias de rutura = stock total atual / consumo diário.
      </div>
    ),
    []
  );

  const tooltipMax2Meses = useMemo(
    () => (
      <div style={{ fontSize: 11, maxWidth: 360 }}>
        <strong>Dias Rutura (prev. mês seg.)</strong>
        <br />• Considera a sazonalidade do <u>mês seguinte</u> ao atual;
        <br />
        • Procura o mesmo mês no último ano;
        <br />
        • Consumo diário = vendas desse mês / 30;
        <br />• Dias de rutura = stock total atual / consumo diário sazonal.
      </div>
    ),
    []
  );

  const carregarImagensMateriais = async (refs) => {
    const lista = Array.from(
      new Set((refs || []).map((r) => String(r || "").trim()).filter(Boolean))
    );

    const faltam = lista.filter((r) => !imgsByRef[r]);
    if (faltam.length === 0) return;

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", { refs: faltam })
    );

    if (err) return;

    const mapa = {};
    (Array.isArray(dados) ? dados : []).forEach((x) => {
      const ref = String(x?.refmaterial || "").trim();
      const url = String(x?.url || "").trim();
      if (ref && url) mapa[ref] = url;
    });

    setImgsByRef((prev) => ({ ...(prev || {}), ...mapa }));
  };

  // ---------------------------------------------------------------------------
  // Carga da lista
  // ---------------------------------------------------------------------------
  const carregarMateriaisRisco = async () => {
    if (!podeVerRuturas) return;

    setLoading(true);

    try {
      const body = {
        diasVendas: 30, // por agora fixo
        mostrarInativos: mostrarInativos ? 1 : 0,
      };

      const [dados, err] = await handleApi(api.post("Materiais/SelAnaliseStocksDashboard", body));

      if (err) {
        showAlert(err, "error");
        setMateriaisRisco([]);
        return;
      }

      // o handleApi já devolve diretamente o `data` do envelope
      if (!Array.isArray(dados)) {
        console.log("Resposta inesperada de SelAnaliseStocksDashboard:", dados);
        setMateriaisRisco([]);
        return;
      }

      // console.log("SelAnaliseStocksDashboard – materiais", dados.length);
      setMateriaisRisco(dados);

      carregarImagensMateriais(dados.map((m) => m.refmaterial));
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar materiais com risco de rutura.", "error");
      setMateriaisRisco([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarMateriaisRisco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVerRuturas, mostrarInativos]);

  if (!podeVerRuturas) return null;

  // ---------------------------------------------------------------------------
  // INATIVAR MATERIAL
  // ---------------------------------------------------------------------------
  const abrirDialogInativar = (mat) => {
    setMaterialRiscoSel(mat);
    setOpenDialogInativar(true);
  };

  const handleCancelarInativar = () => {
    setOpenDialogInativar(false);
    setMaterialRiscoSel(null);
  };

  const handleConfirmarInativar = async () => {
    if (!materialRiscoSel) return;

    try {
      const body = {
        refmaterial: materialRiscoSel.refmaterial,
        inativo: 1,
      };

      const res = await api.post("Materiais/UpdMaterialInativo", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Material inativado com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) => {
        const atualizada = prev.map((m) =>
          m.refmaterial === materialRiscoSel.refmaterial ? { ...m, inativo: 1 } : m
        );

        // esconder imediatamente se o switch estiver desligado
        return mostrarInativos ? atualizada : atualizada.filter((m) => m.inativo !== 1);
      });
    } catch {
      showAlert("Erro ao inativar material.", "error");
    } finally {
      handleCancelarInativar();
    }
  };

  // ---------------------------------------------------------------------------

  const handleOpenDetalhes = (row) => {
    if (!row) return;

    setMaterialDetalhe({
      refmaterial: row.refmaterial,
      ref: row.refmaterial,
      nome: row.nomematerial,
    });
    setDetalhesOpen(true);
  };

  const handleCloseDetalhes = () => {
    setDetalhesOpen(false);
    setMaterialDetalhe(null);
  };

  // ---------------------------------------------------------------------------
  // ALTERAR DIAS DE RUTURA
  // ---------------------------------------------------------------------------
  const handleChangeDiasRutura = (refmaterial, value) => {
    const limpo = value.replace(/[^\d]/g, "");
    setMateriaisRisco((prev) =>
      prev.map((m) => (m.refmaterial === refmaterial ? { ...m, diasrutura_temp: limpo } : m))
    );
  };

  const handleBlurDiasRutura = (row) => {
    // procurar o material atual na lista
    const matAtual = materiaisRisco.find((m) => m.refmaterial === row.refmaterial) || row;

    const atual = matAtual.diasrutura ?? 30;
    const tempStr =
      matAtual.diasrutura_temp !== undefined ? matAtual.diasrutura_temp : String(atual);

    if (tempStr === "") {
      // limpa → reverte
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    const novo = parseInt(tempStr, 10);
    if (!Number.isFinite(novo) || novo < 0) {
      // inválido → reverte
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      showAlert("Valor de dias de rutura inválido.", "error");
      return;
    }

    // SE O VALOR NÃO MUDOU → NÃO ABRE DIALOG
    if (novo === atual) {
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    // abre diálogo de confirmação
    setDialogDiasRutura({
      open: true,
      refmaterial: matAtual.refmaterial,
      nome: matAtual.nomematerial,
      valorAntigo: atual,
      valorNovo: novo,
    });
  };

  const handleCancelarDiasRutura = () => {
    // reverter campo temp
    setMateriaisRisco((prev) =>
      prev.map((m) =>
        m.refmaterial === dialogDiasRutura.refmaterial ? { ...m, diasrutura_temp: undefined } : m
      )
    );
    setDialogDiasRutura((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmarDiasRutura = async () => {
    const { refmaterial, valorNovo } = dialogDiasRutura;
    try {
      const body = {
        refmaterial,
        diasrutura: valorNovo,
      };

      const res = await api.post("Materiais/UpdMaterialDiasRutura", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Dias de rutura atualizados com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === refmaterial
            ? { ...m, diasrutura: valorNovo, diasrutura_temp: undefined }
            : m
        )
      );
    } catch {
      showAlert("Erro ao atualizar dias de rutura.", "error");
    } finally {
      setDialogDiasRutura((prev) => ({ ...prev, open: false }));
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const cabecalhos = [
    "Fabricante",
    "", // miniatura
    "Refª Material",
    "Nome do material",
    "Qtd. Stock Total",
    "Consumo diário (média ano)",
    "Dias Rutura (média ano)",
    "Consumo diário (prev. mês seg.)",
    "Dias Rutura (prev. mês seg.)",
  ];

  return (
    <>
      <MDBox mt={2} mb={2}>
        <Card>
          <MDBox p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
            <MDBox>
              <MDTypography variant="h6" gutterBottom>
                Materiais com risco de rutura
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Lista de mercadorias (FERT) em que pelo menos uma das colunas{" "}
                <strong>"Dias Rutura"</strong> está abaixo do limiar definido no material.
              </MDTypography>
            </MDBox>

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarInativos}
                  onChange={(e) => setMostrarInativos(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <MDTypography variant="button" color="text">
                  Mostrar materiais inativos
                </MDTypography>
              }
              sx={{ ml: 2 }}
            />
          </MDBox>

          <MDBox px={2} pb={2}>
            {loading ? (
              <MDBox py={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </MDBox>
            ) : (
              <MDBox sx={{ maxHeight: "45vh", overflow: "auto", borderRadius: 1 }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      {/* Coluna dos botões / dias rutura */}
                      <th
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          background: "#fff",
                          textAlign: "left",
                          padding: "6px 8px",
                          fontSize: 12,
                          color: "#7b809a",
                          verticalAlign: "bottom",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                        }}
                      />
                      {cabecalhos.map((titulo) => (
                        <th
                          key={titulo}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          {titulo === "Dias Rutura (média ano)" ? (
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                              Dias Rutura (média ano)
                              <Tooltip title={tooltipMediaAno} arrow>
                                <InfoIcon sx={{ fontSize: 16, cursor: "pointer", ml: 0.5 }} />
                              </Tooltip>
                            </span>
                          ) : titulo === "Dias Rutura (máx. 2 meses)" ? (
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                              Dias Rutura (máx. 2 meses)
                              <Tooltip title={tooltipMax2Meses} arrow>
                                <InfoIcon sx={{ fontSize: 16, cursor: "pointer", ml: 0.5 }} />
                              </Tooltip>
                            </span>
                          ) : (
                            titulo
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {materiaisRisco.map((mat) => {
                      const limiar = mat.diasrutura ?? 30;

                      const consumoMedia = mat.consumo_diario_media_ano;
                      const diasMedia = mat.dias_rutura_media_ano;

                      const consumoMesSeg = mat.consumo_diario_mes_seg;
                      const diasMax2Meses = mat.dias_rutura_max_2meses;

                      const unidade = mat.unidade || "UN";

                      return (
                        <tr key={mat.refmaterial}>
                          {/* BOTÕES / DIAS RUTURA EDITÁVEL */}
                          <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="info"
                                onClick={() => handleOpenDetalhes(mat)}
                                sx={{ minWidth: 70, fontSize: 11, py: 0.25 }}
                              >
                                Detalhes
                              </MDButton>

                              <MDButton
                                size="small"
                                variant="outlined"
                                color={mat.inativo === 1 ? "success" : "error"}
                                onClick={() =>
                                  mat.inativo === 1
                                    ? abrirDialogAtivar(mat)
                                    : abrirDialogInativar(mat)
                                }
                                sx={{ minWidth: 72, fontSize: 11, py: 0.25 }}
                              >
                                {mat.inativo === 1 ? "Ativar" : "Inativar"}
                              </MDButton>

                              <TextField
                                value={mat.diasrutura_temp ?? mat.diasrutura ?? 30}
                                onChange={(e) =>
                                  handleChangeDiasRutura(mat.refmaterial, e.target.value)
                                }
                                onBlur={() => handleBlurDiasRutura(mat)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault(); // impede submit ou comportamentos estranhos
                                    handleBlurDiasRutura(mat); // ativa o mesmo fluxo que o blur
                                  }
                                }}
                                size="small"
                                sx={{ width: 60 }}
                                inputProps={{
                                  style: { textAlign: "center", fontSize: 12, padding: "4px 6px" },
                                }}
                              />
                            </MDBox>
                          </td>

                          {/* FABRICANTE */}
                          <td
                            style={{
                              padding: "6px 8px",
                              maxWidth: 160,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={mat.fabricante} // tooltip com o nome completo
                          >
                            {mat.fabricante || "-"}
                          </td>

                          {/* IMG */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {(() => {
                              const ref = String(mat.refmaterial || "").trim();
                              const url = imgsByRef?.[ref] || "";
                              if (!url)
                                return <span style={{ display: "inline-block", width: 32 }} />;

                              return (
                                <img
                                  src={url}
                                  alt={ref}
                                  title="Abrir imagem"
                                  style={{
                                    width: 26,
                                    height: 26,
                                    objectFit: "contain",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                    border: "1px solid #eee",
                                    background: "#fff",
                                  }}
                                  onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                />
                              );
                            })()}
                          </td>

                          {/* REFª MATERIAL */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {mat.refmaterial}
                          </td>

                          {/* NOME */}
                          <td style={{ padding: "6px 8px" }}>{mat.nomematerial}</td>

                          {/* QTD STOCK TOTAL */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {mat.qtdstocktotal != null
                              ? `${Number(mat.qtdstocktotal).toLocaleString("pt-PT", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })} ${unidade}`
                              : "-"}
                          </td>

                          {/* CONSUMO DIÁRIO (MÉDIA ANO) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {consumoMedia == null
                              ? "-"
                              : `${Number(consumoMedia).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 3,
                                })} ${unidade}`}
                          </td>

                          {/* DIAS RUTURA (MÉDIA ANO) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {diasMedia == null ? (
                              "-"
                            ) : (
                              <span
                                style={{
                                  color: diasMedia < limiar ? "#f44335" : "inherit",
                                  fontWeight: diasMedia < limiar ? 600 : 400,
                                }}
                              >
                                {Number(diasMedia).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 1,
                                })}{" "}
                                dias
                              </span>
                            )}
                          </td>

                          {/* CONSUMO DIÁRIO (MÊS SEG.) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {consumoMesSeg == null
                              ? "-"
                              : `${Number(consumoMesSeg).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 3,
                                })} ${unidade}`}
                          </td>

                          {/* DIAS RUTURA (MÁX. 2 MESES) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {diasMax2Meses == null ? (
                              "-"
                            ) : (
                              <span
                                style={{
                                  color: diasMax2Meses < limiar ? "#f44335" : "inherit",
                                  fontWeight: diasMax2Meses < limiar ? 600 : 400,
                                }}
                              >
                                {Number(diasMax2Meses).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 1,
                                })}{" "}
                                dias
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            )}
          </MDBox>

          {/* Dialog — Stocks/Vendas/Compras (MenuSearchMateriais) */}
          <MenuSearchMateriais
            open={detalhesOpen}
            onClose={handleCloseDetalhes}
            material={materialDetalhe}
          />
        </Card>
      </MDBox>

      {/* Dialog — confirmar inativação */}
      <Dialog open={openDialogInativar} onClose={handleCancelarInativar} maxWidth="xs" fullWidth>
        <DialogTitle>Inativar material</DialogTitle>
        <DialogContent dividers>
          {materialRiscoSel && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende inativar o material:
              </MDTypography>
              <MDTypography fontWeight="bold">{materialRiscoSel.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialRiscoSel.nomematerial}
              </MDTypography>
              <MDTypography variant="body2" color="error">
                Esta ação não pode ser anulada.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarInativar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarInativar} color="error" variant="contained">
            Inativar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — confirmar alteração de dias de rutura */}
      <Dialog
        open={dialogDiasRutura.open}
        onClose={handleCancelarDiasRutura}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleConfirmarDiasRutura();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            handleCancelarDiasRutura();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Alterar dias de rutura</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" mb={1}>
            Confirmar alteração dos dias de rutura do material:
          </MDTypography>
          <MDTypography fontWeight="bold">
            {dialogDiasRutura.refmaterial} — {dialogDiasRutura.nome}
          </MDTypography>
          <MDTypography variant="body2" mt={2}>
            De <strong>{dialogDiasRutura.valorAntigo}</strong> para{" "}
            <strong>{dialogDiasRutura.valorNovo}</strong> dias.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarDiasRutura} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarDiasRutura} color="info" variant="contained">
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — confirmar ativação */}
      <Dialog open={openDialogAtivar} onClose={handleCancelarAtivar} maxWidth="xs" fullWidth>
        <DialogTitle>Ativar material</DialogTitle>
        <DialogContent dividers>
          {materialRiscoSel && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende ativar o material:
              </MDTypography>
              <MDTypography fontWeight="bold">{materialRiscoSel.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialRiscoSel.nomematerial}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarAtivar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarAtivar} color="success" variant="contained">
            Ativar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
